import type { DashboardTab } from "@/types/dashboard";
import type { PageContentSearchPayload } from "@/types/pageContentSearch";
import { ALL_DASHBOARD_TABS, resolveDashboardHash } from "@/nav/navConfig";

/** UI actions returned by the assistant API (matches v1 `__CREA_ASSISTANT_EXECUTE__`). */
export type AssistantUiAction =
  | { type: "set_tab"; tab?: string }
  | { type: "navigate_tab"; tab?: string }
  | { type: "open_lead_by_id"; leadId?: string }
  | { type: "open_lead_by_identity"; userId?: string; email?: string }
  | { type: "open_page_stats"; path?: string }
  | { type: "open_request_log_entry"; logId?: string }
  | { type: "open_recording"; recordingId?: string }
  | { type: "show_table"; tableId?: string; title?: string; subtitle?: string; columns?: { key: string; label?: string }[]; rows?: Record<string, unknown>[] }
  | {
      type: "show_page_content_search";
      searchId?: string;
      payload: PageContentSearchPayload;
    };

export type AssistantMutationConfirmation = {
  id: string;
  tool: string;
  args: Record<string, unknown>;
  label: string;
};

export type AssistantContext = {
  surface: "dashboard" | "account";
  tab: string;
  domain: string;
  hash?: string;
  path?: string;
  userEmail?: string;
  userRole?: string;
  isAdministrator?: boolean;
  selectedLeadId?: string | null;
};

const VALID_TABS = new Set<string>(ALL_DASHBOARD_TABS);

export function normalizeAssistantTab(tab: string | undefined): DashboardTab {
  const t = String(tab || "").trim().toLowerCase();
  if (t === "pages" || t === "site_structure") return "site-structure";
  if (t === "site-usage") return "cache";
  if (t === "experiments" || t === "ab_tests") return "ab-tests";
  if (t === "live-visitors" || t === "live_users") return "live-users";
  if (t === "usage") return "billing";
  if (t === "origin-queue") return "queue";
  if (VALID_TABS.has(t)) return t as DashboardTab;
  return resolveDashboardHash(t);
}

export function normalizeAssistantAction(raw: unknown): AssistantUiAction | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const type = String(a.type || "").trim();
  if (type === "navigate_tab") {
    return { type: "set_tab", tab: String(a.tab || "") };
  }
  if (type === "set_tab") return { type: "set_tab", tab: String(a.tab || "") };
  if (type === "open_lead_by_id") return { type: "open_lead_by_id", leadId: String(a.leadId || "") };
  if (type === "open_lead_by_identity") {
    return {
      type: "open_lead_by_identity",
      userId: String(a.userId || ""),
      email: String(a.email || "")
    };
  }
  if (type === "open_page_stats") return { type: "open_page_stats", path: String(a.path || "/") };
  if (type === "open_request_log_entry") return { type: "open_request_log_entry", logId: String(a.logId || "") };
  if (type === "open_recording") return { type: "open_recording", recordingId: String(a.recordingId || "") };
  if (type === "show_table") return raw as AssistantUiAction;
  if (type === "show_page_content_search") {
    const payload = (raw as { payload?: PageContentSearchPayload }).payload;
    if (!payload || typeof payload !== "object") return null;
    return {
      type: "show_page_content_search",
      searchId: String((raw as { searchId?: string }).searchId || ""),
      payload
    };
  }
  return null;
}

export const ASSISTANT_PENDING_KEY = "__CREA_ASSISTANT_PENDING__";
export const ASSISTANT_PENDING_EVENT = "crea:assistant-pending";

declare global {
  interface Window {
    __CREA_ASSISTANT_EXECUTE__?: (actions: unknown[]) => void;
    __CREA_ASSISTANT_CONTEXT__?: () => AssistantContext;
    __CREA_ASSISTANT_SHOW_TABLE__?: (spec: unknown) => void;
    __CREA_ASSISTANT_SHOW_CONTENT_SEARCH__?: (spec: {
      searchId?: string;
      payload: PageContentSearchPayload;
    }) => void;
  }
}
